// ─── GST Invoice Calculation Helpers ────────────────────────────────────────

export interface InvoiceCalculations {
  systemBase: number;
  serviceBase: number;
  systemCGST: number;
  systemSGST: number;
  serviceCGST: number;
  serviceSGST: number;
  sgstOutput: number;
  cgstOutput: number;
  grandTotal: number;
  taxableBase: number;
  systemTotalTax: number;
  serviceTotalTax: number;
  totalTaxableValue: number;
  totalCGST: number;
  totalSGST: number;
  totalTax: number;
}

const r0  = (n: number) => Math.round(n);               // nearest whole rupee (taxable values)
const r50 = (n: number) => Math.round(n * 2) / 2;       // nearest ₹0.50     (GST amounts)
const r2  = (n: number) => Math.round(n * 100) / 100;   // 2 decimal places   (totals / output)

export function computeInvoice(grandAmount: number): InvoiceCalculations {
  // Step 1: Split grand total 90/10 on inclusive basis
  const systemInclusive  = grandAmount * 0.9;   // 90% includes system cost + 5% GST
  const serviceInclusive = grandAmount * 0.1;   // 10% includes service cost + 18% GST

  // Step 2: Back-calculate taxable (pre-tax) value — rounded to whole rupees
  const systemBase  = r0(systemInclusive  / 1.05);  // ÷1.05  → taxable at 5%  GST
  const serviceBase = r0(serviceInclusive / 1.18);  // ÷1.18  → taxable at 18% GST

  // Step 3: Compute CGST / SGST — rounded to nearest ₹0.50 (Indian GST standard)
  const systemCGST  = r50(systemBase  * 0.025);  // 2.5% CGST on system
  const systemSGST  = r50(systemBase  * 0.025);  // 2.5% SGST on system
  const serviceCGST = r50(serviceBase * 0.09);   // 9%   CGST on service
  const serviceSGST = r50(serviceBase * 0.09);   // 9%   SGST on service

  const sgstOutput = r2(systemSGST + serviceSGST);
  const cgstOutput = r2(systemCGST + serviceCGST);

  const grandTotal = r2(systemBase + serviceBase + sgstOutput + cgstOutput);

  const systemTotalTax = r2(systemCGST + systemSGST);
  const serviceTotalTax = r2(serviceCGST + serviceSGST);
  const totalTaxableValue = r2(systemBase + serviceBase);
  const totalCGST = cgstOutput;
  const totalSGST = sgstOutput;
  const totalTax = r2(totalCGST + totalSGST);

  return {
    systemBase,
    serviceBase,
    systemCGST,
    systemSGST,
    serviceCGST,
    serviceSGST,
    sgstOutput,
    cgstOutput,
    grandTotal,
    taxableBase: r2(systemBase + serviceBase),
    systemTotalTax,
    serviceTotalTax,
    totalTaxableValue,
    totalCGST,
    totalSGST,
    totalTax,
  };
}

const ones = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const tens = [
  "", "", "Twenty", "Thirty", "Forty", "Fifty",
  "Sixty", "Seventy", "Eighty", "Ninety",
];

function twoDigitWords(n: number): string {
  if (n < 20) return ones[n];
  return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
}

function threeDigitWords(n: number): string {
  if (n === 0) return "";
  if (n < 100) return twoDigitWords(n);
  return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigitWords(n % 100) : "");
}

export function numberToWords(amount: number): string {
  const intPart = Math.round(amount);
  if (intPart === 0) return "INR Zero Only";

  let n = intPart;
  const parts: string[] = [];

  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  const rest = n;

  if (crore) parts.push(threeDigitWords(crore) + " Crore");
  if (lakh) parts.push(twoDigitWords(lakh) + " Lakh");
  if (thousand) parts.push(threeDigitWords(thousand) + " Thousand");
  if (rest) parts.push(threeDigitWords(rest));

  return "INR " + parts.join(" ") + " Only";
}

export function fmt(n: number): string {
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
